import { insiderMembershipState } from "@faire/retailer-shared/lib/retailer";
import { useIsLoggedInRetailerOrBrandPreview } from "@faire/retailer-visitor-shared/lib/isLoggedInRetailer";
import { getRetailer } from "@faire/retailer-visitor-shared/serialized-data/getRetailer";
import { useEventsStore } from "@faire/retailer-visitor-shared/stores/domain/Events/shared/EventsStore";
import { useStrictLocalization } from "@faire/web--source/common/localization/localize";
import { MARKET_FALLBACKS } from "@faire/web--source/common/promotional-events/getFaireMarketUtils";
import { useClock } from "@faire/web--source/common/useClock";
import { PromotionalEventType } from "@faire/web-api--source/indigofair/common/PromotionalEventType";
import { ICurrentPromotionalEventResponse } from "@faire/web-api--source/indigofair/data/ICurrentPromotionalEventResponse";
import { IRetailer } from "@faire/web-api--source/indigofair/data/IRetailer";
import { isWithinInterval } from "date-fns/isWithinInterval";
import { useMemo } from "react";

export const useMarketCurrentEvent = () => {
  const { now } = useClock();
  const isLoggedInRetailerOrBrandPreview =
    useIsLoggedInRetailerOrBrandPreview();
  const { strictLocalize } = useStrictLocalization();
  const { eventData: eventDataForEventType, nextOrActive } = useEventsStore([
    "eventData",
    "nextOrActive",
  ]);

  const fallbacks = useMemo(() => {
    return MARKET_FALLBACKS(strictLocalize);
  }, [strictLocalize]);

  const latestMarketEvent = useMemo(() => {
    if (
      nextOrActive?.event?.event_group ===
      ICurrentPromotionalEventResponse.IPromotionalEvent.PromotionalEventGroup
        .MARKET
    ) {
      return nextOrActive;
    }
    return undefined;
  }, [nextOrActive]);

  const currentEventType =
    (latestMarketEvent?.event?.event_type as PromotionalEventType) ??
    fallbacks.currentEventType;
  const marketName = latestMarketEvent?.event?.event_name ?? fallbacks.name;

  const {
    insider_early_access_start_at:
      earlyAccessStartAt = fallbacks.earlyAccessStartAtTimestamp,
    start_at: mainEventStartAt = fallbacks.mainEventStartAtTimestamp,
    end_at: mainEventEndAt = fallbacks.endAtTimestamp,
  } = useMemo(() => {
    return (
      eventDataForEventType(currentEventType) ??
      ICurrentPromotionalEventResponse.build()
    );
  }, [eventDataForEventType, currentEventType]);

  const isLive = useMemo(() => {
    if (!isLoggedInRetailerOrBrandPreview) {
      return false;
    }

    if (
      isWithinInterval(now, {
        start: earlyAccessStartAt,
        end: mainEventStartAt,
      })
    ) {
      return (
        insiderMembershipState(getRetailer()) ===
        IRetailer.InsiderMembershipState.ACTIVE
      );
    }

    return isWithinInterval(now, {
      start: mainEventStartAt,
      end: mainEventEndAt,
    });
  }, [
    isLoggedInRetailerOrBrandPreview,
    now,
    earlyAccessStartAt,
    mainEventStartAt,
    mainEventEndAt,
  ]);

  return { currentEventType, isLive, marketName };
};
